import { Component, type ErrorInfo, type ReactNode } from 'react';

import styles from './ErrorBoundary.module.css';

interface Props {
  children: ReactNode;
  /** Фолбэк-рендер; по умолчанию — минимальный экран с перезагрузкой. */
  fallback?: (reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Последний рубеж. Текст здесь намеренно не через t(): если упал сам провайдер
 * i18n, вызов t() упадёт повторно и мы получим белый экран вместо сообщения.
 * Поэтому — нейтральная нетекстовая графика и кнопка перезагрузки без подписи,
 * а не непереведённая строка.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Токены и адреса в сообщения об ошибках не попадают — логируем только стек.
    console.error('[Continuity] render error', error.message, info.componentStack);
  }

  private readonly reset = (): void => {
    this.setState({ hasError: false });
  };

  private readonly reload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset);

    return (
      <div className={styles.root} role="alert">
        <div className={styles.ring} aria-hidden="true" />
        <button type="button" className={styles.action} onClick={this.reload}>
          <span className={styles.arrow} aria-hidden="true" />
        </button>
      </div>
    );
  }
}
